import { RootState } from '@AppState/redux-store'
import { CustomerServiceAPI } from '@API/data-services/customer-service'
import { blankToNull } from '@Components/games/utils'
import {
  AuthScopes,
  CustomerConfigResponse,
  PlayerDataCustomerConfig,
} from '@Generated/back-office-client/src'
import { ConfigHistoryDrawer } from '@Components/common/config-history-drawer'
import { WithPermissions } from '@Components/common/with-permissions'
import { ConfigHistoryTarget } from './types'
import {
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Link,
  Switch,
  Text,
  TextArea,
  TextField,
} from '@radix-ui/themes'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

type BoEditableConfig = {
  freshdeskId?: string | null
  claimFormInactivityTimeoutMs?: number | null
  logoUrl: string
}

/** The logo as it will look in client apps; a URL that fails to load shows nothing. */
const LogoPreview = ({ url }: { url: string }) => {
  const logoUrl = url.trim()
  const [isBroken, setIsBroken] = useState(false)

  // A URL that failed is only broken until it is edited, so each new one gets a fresh attempt.
  useEffect(() => setIsBroken(false), [logoUrl])

  if (!logoUrl || isBroken) {
    return null
  }
  return (
    <img
      data-testid="customer-logo-preview"
      src={logoUrl}
      alt=""
      style={{ height: 32, maxWidth: 120, objectFit: 'contain', alignSelf: 'flex-start' }}
      onError={() => setIsBroken(true)}
    />
  )
}

export const CustomerConfig = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const queryClient = useQueryClient()

  const [boConfig, setBoConfig] = useState<BoEditableConfig>({ logoUrl: '' })
  const [pdConfig, setPdConfig] = useState<PlayerDataCustomerConfig>({})
  const [historyOpen, setHistoryOpen] = useState<ConfigHistoryTarget | null>(null)

  const boQueryKey = ['customer-config', activeCustomerId]
  const pdQueryKey = ['player-data-config', activeCustomerId]

  const {
    data: boData,
    isLoading: boLoading,
    isError: boIsError,
    error: boFetchError,
  } = useQuery({
    queryKey: boQueryKey,
    queryFn: () => CustomerServiceAPI.getCustomerConfig(),
    enabled: !!activeCustomerId,
  })

  const {
    data: pdData,
    isLoading: pdLoading,
    isError: pdIsError,
    error: pdFetchError,
  } = useQuery({
    queryKey: pdQueryKey,
    queryFn: () => CustomerServiceAPI.getPlayerDataConfig(activeCustomerId!),
    enabled: !!activeCustomerId,
  })

  // Initialize the editable BO draft state when query data lands.
  useEffect(() => {
    if (boData) {
      setBoConfig({
        freshdeskId: boData.freshdeskId ?? null,
        claimFormInactivityTimeoutMs: boData.claimFormInactivityTimeoutMs ?? null,
        logoUrl: boData.branding?.logoUrl ?? '',
      })
    }
  }, [boData])

  // Initialize the editable PD draft state when query data lands.
  useEffect(() => {
    if (pdData) {
      setPdConfig(pdData)
    }
  }, [pdData])

  const {
    mutate: saveBoConfig,
    isPending: boSaving,
    isError: boSaveIsError,
    error: boSaveError,
  } = useMutation({
    mutationFn: () => {
      if (!activeCustomerId) {
        return Promise.reject(new Error('No active customer'))
      }
      // Only a changed logo is sent, so a save after the logo failed to load cannot clear it.
      const logoUrl = blankToNull(boConfig.logoUrl)
      return CustomerServiceAPI.patchCustomerConfig(activeCustomerId, {
        freshdeskId: boConfig.freshdeskId ?? undefined,
        claimFormInactivityTimeoutMs: boConfig.claimFormInactivityTimeoutMs ?? undefined,
        ...(logoUrl !== (boData?.branding?.logoUrl ?? null) && { branding: { logoUrl } }),
      })
    },
    onSuccess: (updated) => {
      queryClient.setQueryData<CustomerConfigResponse>(boQueryKey, (existing) => ({
        ...(existing ?? { timezone: '' }),
        ...updated,
      }))
    },
  })

  const {
    mutate: savePdConfig,
    isPending: pdSaving,
    isError: pdSaveIsError,
    error: pdSaveError,
  } = useMutation({
    mutationFn: () => {
      if (!activeCustomerId) {
        return Promise.reject(new Error('No active customer'))
      }
      return CustomerServiceAPI.patchPlayerDataConfig(activeCustomerId, {
        claimFormEnabled: pdConfig.claimFormEnabled ?? false,
        allowDeviceTicketScans: pdConfig.allowDeviceTicketScans ?? false,
        ticketWalletEnabled: pdConfig.ticketWalletEnabled ?? false,
        ticketWalletVisibilityDays: pdConfig.ticketWalletVisibilityDays ?? null,
        scannerGoToWalletCtaEnabled: pdConfig.scannerGoToWalletCtaEnabled ?? false,
        emailSender: pdConfig.emailSender,
        smsSender: pdConfig.smsSender,
        appHostName: pdConfig.appHostName,
        claimsReservationHostUrl: pdConfig.claimsReservationHostUrl,
        maxTicketsPerReservation: pdConfig.maxTicketsPerReservation ?? null,
        firebaseServiceAccountJson: pdConfig.firebaseServiceAccountJson,
        clerk: pdConfig.clerk,
        pamEmailsEnabled: pdConfig.pamEmailsEnabled ?? false,
        emailBranding: pdConfig.emailBranding,
      })
    },
    onSuccess: (updated: PlayerDataCustomerConfig) => {
      queryClient.setQueryData(pdQueryKey, updated)
    },
  })

  const errorMessage = (fetchError: unknown, saveError: unknown): string | null => {
    if (fetchError) {
      return fetchError instanceof Error ? fetchError.message : 'Failed to load'
    }
    if (saveError) {
      return saveError instanceof Error ? saveError.message : 'Failed to save'
    }
    return null
  }

  const boErrorMessage = errorMessage(boIsError && boFetchError, boSaveIsError && boSaveError)
  const pdErrorMessage = errorMessage(pdIsError && pdFetchError, pdSaveIsError && pdSaveError)

  return (
    <Container>
      <Flex direction="column" gap="4">
        <Heading>Customer Config</Heading>

        <Card>
          <Flex direction="column" gap="3">
            <Flex align="center" justify="between">
              <Heading size="4">Back Office Settings</Heading>
              <WithPermissions requiredPermissions={AuthScopes.writecustomer_config}>
                <Button
                  variant="soft"
                  onClick={() => setHistoryOpen(ConfigHistoryTarget.BACK_OFFICE)}>
                  History
                </Button>
              </WithPermissions>
            </Flex>

            {boLoading ? (
              <Text>Loading...</Text>
            ) : (
              <>
                <Box>
                  <Text as="label" size="2" weight="medium">
                    Freshdesk ID
                  </Text>
                  <TextField.Root
                    value={boConfig.freshdeskId ?? ''}
                    onChange={(e) =>
                      setBoConfig({
                        ...boConfig,
                        freshdeskId: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Claim form inactivity timeout (ms)
                  </Text>
                  <TextField.Root
                    type="number"
                    value={
                      boConfig.claimFormInactivityTimeoutMs == null
                        ? ''
                        : String(boConfig.claimFormInactivityTimeoutMs)
                    }
                    onChange={(e) =>
                      setBoConfig({
                        ...boConfig,
                        claimFormInactivityTimeoutMs:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Lottery logo URL
                  </Text>
                  <TextField.Root
                    value={boConfig.logoUrl}
                    onChange={(e) => setBoConfig({ ...boConfig, logoUrl: e.target.value })}
                  />
                  <Text as="p" size="1" color="gray" mt="1">
                    The lottery&apos;s logo, shown in the header of client apps such as web
                    playslips.
                  </Text>
                </Box>

                <LogoPreview url={boConfig.logoUrl} />

                {boErrorMessage && (
                  <Text color="red" size="2">
                    {boErrorMessage}
                  </Text>
                )}

                <Flex>
                  <Button onClick={() => saveBoConfig()} disabled={boSaving} loading={boSaving}>
                    Save
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Card>

        <Card>
          <Flex direction="column" gap="3">
            <Flex align="center" justify="between">
              <Heading size="4">Player Data Settings</Heading>
              <WithPermissions requiredPermissions={AuthScopes.writecustomer_config}>
                <Button
                  variant="soft"
                  onClick={() => setHistoryOpen(ConfigHistoryTarget.PLAYER_DATA)}>
                  History
                </Button>
              </WithPermissions>
            </Flex>

            {pdLoading ? (
              <Text>Loading...</Text>
            ) : (
              <>
                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.claimFormEnabled ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, claimFormEnabled: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Claim form enabled
                  </Text>
                </Flex>

                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.allowDeviceTicketScans ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, allowDeviceTicketScans: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Allow device-only ticket scans
                  </Text>
                </Flex>

                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.ticketWalletEnabled ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, ticketWalletEnabled: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Ticket Wallet enabled
                  </Text>
                </Flex>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Ticket wallet visibility (days)
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Hide tickets this many days after their last draw. Leave blank to keep all
                    tickets.
                  </Text>
                  <TextField.Root
                    type="number"
                    value={
                      pdConfig.ticketWalletVisibilityDays == null
                        ? ''
                        : String(pdConfig.ticketWalletVisibilityDays)
                    }
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        ticketWalletVisibilityDays:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </Box>

                <Box>
                  <Flex align="center" gap="2">
                    <Switch
                      checked={pdConfig.scannerGoToWalletCtaEnabled ?? false}
                      onCheckedChange={(checked) =>
                        setPdConfig({ ...pdConfig, scannerGoToWalletCtaEnabled: checked })
                      }
                    />
                    <Text as="label" size="2" weight="medium">
                      Scanner &quot;go to wallet&quot; button
                    </Text>
                  </Flex>
                  <Text as="p" size="1" color="gray" mt="1">
                    Show a button linking to the wallet when a scanned ticket is already saved. Keep
                    this off until every released app version can handle it.
                  </Text>
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Email sender
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailSender ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailSender: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    SMS sender
                  </Text>
                  <TextField.Root
                    value={pdConfig.smsSender ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        smsSender: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    App host name
                  </Text>
                  <TextField.Root
                    value={pdConfig.appHostName ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        appHostName: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Claims reservation host URL
                  </Text>
                  <TextField.Root
                    value={pdConfig.claimsReservationHostUrl ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        claimsReservationHostUrl: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Max tickets per reservation
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Maximum tickets a player can select for a claim appointment. Leave blank to use
                    the default (15).
                  </Text>
                  <TextField.Root
                    type="number"
                    value={
                      pdConfig.maxTicketsPerReservation == null
                        ? ''
                        : String(pdConfig.maxTicketsPerReservation)
                    }
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        maxTicketsPerReservation:
                          e.target.value === '' ? null : Number(e.target.value),
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Firebase service account JSON
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Paste the contents of the JSON file from Firebase Console → Project Settings →
                    Service Accounts → Generate new private key. See the{' '}
                    <Link
                      href="https://firebase.google.com/docs/admin/setup#initialize-sdk"
                      target="_blank"
                      rel="noopener noreferrer">
                      Firebase Admin SDK setup docs
                    </Link>
                    .
                  </Text>
                  <TextArea
                    rows={8}
                    value={pdConfig.firebaseServiceAccountJson ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        firebaseServiceAccountJson: e.target.value === '' ? null : e.target.value,
                      })
                    }
                  />
                </Box>

                <Heading size="3">Clerk</Heading>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Publishable key
                  </Text>
                  <TextField.Root
                    value={pdConfig.clerk?.publishableKey ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        clerk: {
                          ...pdConfig.clerk,
                          publishableKey: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Secret key
                  </Text>
                  <TextField.Root
                    value={pdConfig.clerk?.secretKey ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        clerk: {
                          ...pdConfig.clerk,
                          secretKey: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Webhook secret
                  </Text>
                  <TextField.Root
                    value={pdConfig.clerk?.webhookSecret ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        clerk: {
                          ...pdConfig.clerk,
                          webhookSecret: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Heading size="3">Account emails</Heading>

                <Flex align="center" gap="2">
                  <Switch
                    checked={pdConfig.pamEmailsEnabled ?? false}
                    onCheckedChange={(checked) =>
                      setPdConfig({ ...pdConfig, pamEmailsEnabled: checked })
                    }
                  />
                  <Text as="label" size="2" weight="medium">
                    Account email notifications enabled
                  </Text>
                </Flex>
                <Text as="p" size="1" color="gray">
                  Sends account emails for KYC outcomes and document uploads. Nothing is sent unless
                  an email sender is set above.
                </Text>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Brand color
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Hex color for the header band and buttons, e.g. #1E2939. A value that is not
                    valid hex is ignored and a neutral default is used.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.brandColor ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          brandColor: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Hero image URL
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Publicly hosted banner shown at the top of every account email. JPEG or PNG only
                    — mail clients strip SVG. Assets are cached hard, so publish a new versioned
                    filename rather than overwriting one. Leave blank for a plain header.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.heroImageUrl ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          heroImageUrl: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Hero icon URL
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Logo or icon overlaid on the header band. Same rules as the hero image; PNG when
                    it needs transparency.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.heroIconUrl ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          heroIconUrl: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Site URL
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Target for the call-to-action button. Leave blank and the button is omitted
                    entirely.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.siteUrl ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          siteUrl: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Site display name
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Link text for the site URL in the footer. Only shown when a site URL is also
                    set.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.siteDisplayName ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          siteDisplayName: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Postal address
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Appended to the copyright line in the footer.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.postalAddress ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          postalAddress: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                <Box>
                  <Text as="label" size="2" weight="medium">
                    Privacy policy URL
                  </Text>
                  <Text as="p" size="1" color="gray" mb="1">
                    Adds a Privacy Policy link to the footer. Leave blank to omit it.
                  </Text>
                  <TextField.Root
                    value={pdConfig.emailBranding?.privacyUrl ?? ''}
                    onChange={(e) =>
                      setPdConfig({
                        ...pdConfig,
                        emailBranding: {
                          ...pdConfig.emailBranding,
                          privacyUrl: e.target.value === '' ? null : e.target.value,
                        },
                      })
                    }
                  />
                </Box>

                {pdErrorMessage && (
                  <Text color="red" size="2">
                    {pdErrorMessage}
                  </Text>
                )}

                <Flex>
                  <Button onClick={() => savePdConfig()} disabled={pdSaving} loading={pdSaving}>
                    Save
                  </Button>
                </Flex>
              </>
            )}
          </Flex>
        </Card>
      </Flex>

      {historyOpen === ConfigHistoryTarget.BACK_OFFICE && activeCustomerId && (
        <ConfigHistoryDrawer
          title="Back Office Settings history"
          queryKey={['customer-config-history', activeCustomerId]}
          listHistory={() => CustomerServiceAPI.listCustomerConfigHistory(activeCustomerId)}
          getVersion={(changeId) =>
            CustomerServiceAPI.getCustomerConfigVersion(activeCustomerId, changeId)
          }
          restoreVersion={(changeId) =>
            CustomerServiceAPI.restoreCustomerConfigVersion(activeCustomerId, changeId)
          }
          onRestored={() => queryClient.invalidateQueries({ queryKey: boQueryKey })}
          onClose={() => setHistoryOpen(null)}
        />
      )}

      {historyOpen === ConfigHistoryTarget.PLAYER_DATA && activeCustomerId && (
        <ConfigHistoryDrawer
          title="Player Data Settings history"
          queryKey={['player-data-config-history', activeCustomerId]}
          listHistory={() => CustomerServiceAPI.listPlayerDataConfigHistory(activeCustomerId)}
          getVersion={(changeId) =>
            CustomerServiceAPI.getPlayerDataConfigVersion(activeCustomerId, changeId)
          }
          restoreVersion={(changeId) =>
            CustomerServiceAPI.restorePlayerDataConfigVersion(activeCustomerId, changeId)
          }
          onRestored={() => queryClient.invalidateQueries({ queryKey: pdQueryKey })}
          onClose={() => setHistoryOpen(null)}
        />
      )}
    </Container>
  )
}
